import React, { useState, useEffect } from 'react';
import { ExternalLink, Handshake } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SponsorsSection = () => {
  const [sponsors, setSponsors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cms/sponsor-heroes`);
        if (response.ok) {
          const data = await response.json();
          setSponsors(data.filter(s => s.is_active !== false));
        }
      } catch (err) {
        console.error('Failed to fetch sponsors:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSponsors();
  }, []);

  useEffect(() => {
    const checkTheme = () => {
      const theme = localStorage.getItem('theme') || 'dim';
      setIsDarkMode(theme === 'dark' || theme === 'dim');
    };
    checkTheme();
    window.addEventListener('storage', checkTheme);
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => { window.removeEventListener('storage', checkTheme); observer.disconnect(); };
  }, []);

  const getLogoUrl = (sponsor) => {
    if (isDarkMode) return sponsor.logo_url_dark || sponsor.logo_url;
    return sponsor.logo_url_light || sponsor.logo_url;
  };

  if (isLoading || sponsors.length === 0) return null;

  const tierOrder = { presenting: 0, headline: 1, supporting: 2, contributing: 3 };
  const sortedSponsors = [...sponsors].sort((a, b) => (tierOrder[a.tier] ?? 99) - (tierOrder[b.tier] ?? 99));

  const getTierLabel = (tier) => {
    switch (tier) {
      case 'presenting': return 'Presenting Sponsor';
      case 'headline': return 'Headline Sponsor';
      case 'supporting': return 'Supporting Sponsor';
      case 'contributing': return 'Contributing Sponsor';
      default: return 'Sponsor';
    }
  };

  const getTierGradient = (tier) => {
    switch (tier) {
      case 'presenting': return 'from-amber-500/20 to-amber-600/10';
      case 'headline': return 'from-primary/20 to-primary/10';
      case 'supporting': return 'from-secondary/20 to-secondary/10';
      default: return 'from-muted to-muted/50';
    }
  };

  return (
    <section className="relative z-10 py-16 px-6 md:px-12 lg:px-24" data-testid="sponsors-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
            Our Sponsors
          </span>
          <h2 className="font-outfit text-3xl md:text-4xl font-semibold tracking-tight">
            Made Possible By <span className="text-gradient-quantum">Our Partners</span>
          </h2>
        </div>

        <div className="glass-card rounded-2xl p-8">
          <div className={`grid gap-8 items-center justify-items-center ${
            sortedSponsors.length === 1 ? 'grid-cols-1' :
            sortedSponsors.length === 2 ? 'grid-cols-2' :
            sortedSponsors.length <= 4 ? 'grid-cols-2 md:grid-cols-4' :
            'grid-cols-2 md:grid-cols-3 lg:grid-cols-6'
          }`}>
            {sortedSponsors.map((sponsor) => (
              <a
                key={sponsor.id}
                href={sponsor.website || '#'}
                target={sponsor.website ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="flex flex-col items-center text-center group w-full"
                data-testid={`sponsor-${sponsor.id}`}
              >
                {getLogoUrl(sponsor) ? (
                  <div className="h-20 w-full flex items-center justify-center mb-3">
                    <img
                      src={getLogoUrl(sponsor)}
                      alt={sponsor.name}
                      className="h-16 w-auto max-w-full object-contain opacity-70 group-hover:opacity-100 transition-opacity"
                    />
                  </div>
                ) : (
                  <div className={`h-20 w-20 rounded-xl bg-gradient-to-br ${getTierGradient(sponsor.tier)} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                    <span className="font-outfit font-bold text-xl text-primary">
                      {sponsor.name.split(' ').map(w => w[0]).join('').slice(0, 3)}
                    </span>
                  </div>
                )}
                <span className="text-sm font-medium text-foreground/80 group-hover:text-foreground transition-colors flex items-center gap-1">
                  {sponsor.name}
                  {sponsor.website && <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                  {getTierLabel(sponsor.tier)}
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SponsorsSection;
